import {
    MsalAuthenticationResult,
    MsalAuthenticationTemplate,
    InteractionType,
    useAzureAuth,
    acquireAccessToken,
} from "@datamole/wds-azure-auth";
import { ReactNode, useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";

import { config } from "@/config";
import { useAppDispatch, useAppSelector } from "@/store";
import { selectAccessToken, setAuth } from "@/store/slices/auth/auth.slice";

const INTERACTION_TYPE = InteractionType.Redirect;

type Props = {
    children: ReactNode;
};

const AuthenticationGuard = ({ children }: Props) => {
    const accessToken = useAppSelector(selectAccessToken);
    const dispatch = useAppDispatch();

    const { isInteractionInProgress, instance } = useAzureAuth({
        interactionType: INTERACTION_TYPE,
    });

    const acquireAndPersistAccessToken = useCallback(async () => {
        const accessToken = await acquireAccessToken(
            {
                scopes: [config.AZURE_AD_AUTH_SCOPE],
            },
            instance
        );

        dispatch(setAuth({ accessToken }));
    }, [instance, dispatch]);

    const ErrorComponent = ({ error }: MsalAuthenticationResult) => {
        console.error(error?.errorMessage);
        return <Navigate to={"/login"} />;
    };

    useEffect(() => {
        /**
         * If user is already logged in or interaction is in progress, do nothing
         */
        if (accessToken || isInteractionInProgress) {
            return;
        }

        /**
         * Otherwise, acquire the token silently
         */
        acquireAndPersistAccessToken();
    }, [isInteractionInProgress, accessToken, acquireAndPersistAccessToken]);

    return (
        <MsalAuthenticationTemplate interactionType={INTERACTION_TYPE} errorComponent={ErrorComponent}>
            {children}
        </MsalAuthenticationTemplate>
    );
};

export { AuthenticationGuard };
